import React from "react";
import { BarChart3, Search, Send } from "lucide-react";
import {
    formatCreditValue,
    getTierLabel,
    getTierName,
} from "../../utils/subscription";

const getUsagePercent = (value, limit) => {
    const numericValue = Number(value || 0);
    const numericLimit = Number(limit || 0);

    if (!numericLimit) return 0;

    return Math.min(100, Math.max(0, (numericValue / numericLimit) * 100));
};

const hasNumericLimit = (limit) =>
    limit !== null && limit !== undefined && Number(limit) > 0;

const formatMetricValue = (value) => {
    if (value === null || value === undefined) {
        return "--";
    }

    return formatCreditValue(value, "--");
};

const formatMetricLimit = (limit) => {
    if (limit === null) {
        return "Unlimited";
    }

    if (limit === undefined) {
        return "--";
    }

    return formatCreditValue(limit, "--");
};

const PlanUsageRow = ({ icon: Icon, label, value, limit, showAsRemaining }) => {
    const showProgress = hasNumericLimit(limit);
    const hasUnlimitedRemaining =
        showAsRemaining && value === null && limit === null;
    const displayValue = hasUnlimitedRemaining
        ? "Unlimited"
        : `${formatMetricValue(value)} / ${formatMetricLimit(limit)}`;

    return (
        <div className="border-b border-slate-100 py-3 last:border-b-0">
            <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Icon size={17} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                        <p className="truncate text-sm font-semibold text-slate-800">
                            {label}
                        </p>
                        <p className="shrink-0 text-sm font-semibold text-slate-600">
                            {displayValue}
                        </p>
                    </div>
                    {showProgress && (
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                                className="h-full rounded-full bg-indigo-600"
                                style={{
                                    width: `${getUsagePercent(value, limit)}%`,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DashboardPlanUsageCard = ({ credits, heightClassName = "h-[330px]" }) => {
    const tierName = credits?.tier_name ? getTierName(credits) : null;
    const tierLabel = tierName ? getTierLabel(tierName) : "--";
    const searchLeadLimit = credits?.search_lead_limit;
    const emailSendLimit = credits?.email_send_limit;

    return (
        <section
            className={`flex ${heightClassName} flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm`}
        >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-indigo-100 bg-[#eef2ff] px-4 py-5 sm:px-6">
                <div>
                    <h2 className="text-base font-semibold text-[#0f172a]">
                        Plan Usage
                    </h2>
                </div>
                <span className="inline-flex max-w-[10rem] shrink-0 items-center truncate rounded-full border border-indigo-200 bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                    {tierLabel}
                </span>
            </div>

            <div className="min-h-0 px-4 sm:px-5 xl:flex-1">
                <PlanUsageRow
                    icon={Search}
                    label="Search leads used"
                    value={credits?.search_leads_used}
                    limit={searchLeadLimit}
                />
                <PlanUsageRow
                    icon={BarChart3}
                    label="Search credits left"
                    value={credits?.search_leads_remaining}
                    limit={searchLeadLimit}
                    showAsRemaining
                />
                <PlanUsageRow
                    icon={Send}
                    label="Email sends used"
                    value={credits?.email_sends_used}
                    limit={emailSendLimit}
                />
            </div>
        </section>
    );
};

export default DashboardPlanUsageCard;
